import { useEffect, useState } from "react"
import { BigNumber, ethers } from "ethers"
import GenArt721CoreV2ABI from "abi/V2/GenArt721CoreV2.json"
import GenArt721CoreV3EngineABI from "abi/V3/GenArt721CoreV3_Engine.json"
import { ArtBlocksChainId } from "contractConfig"
import { makeChainReadCacheKey, readThroughChainCache } from "utils/chainReadCache"
import { getContractChainId, getContractConfigByAddress } from "utils/contractInfoHelper"
import { getReadOnlyProvider } from "utils/readOnlyProvider"

export interface ProjectChainState {
  invocations: number
  maxInvocations: number
  active?: boolean
  paused?: boolean
  complete?: boolean
}

interface ProjectChainStateResponse {
  data?: ProjectChainState
  loading: boolean
  error: boolean
}

const initialState: ProjectChainStateResponse = {
  data: undefined,
  loading: false,
  error: false,
}

const toNumber = (value: unknown) => {
  if (BigNumber.isBigNumber(value)) return value.toNumber()
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const toProjectId = (projectId: string | number | BigInt) => BigNumber.from(projectId.toString())

const readV2ProjectState = async (
  contract: ethers.Contract,
  projectId: string | number | BigInt
): Promise<ProjectChainState> => {
  const tokenInfo = await contract.projectTokenInfo(toProjectId(projectId))
  const invocations = toNumber(tokenInfo.invocations)
  const maxInvocations = toNumber(tokenInfo.maxInvocations)

  return {
    invocations,
    maxInvocations,
    active: Boolean(tokenInfo.active),
    complete: maxInvocations > 0 && invocations >= maxInvocations,
  }
}

const readV3ProjectState = async (
  contract: ethers.Contract,
  projectId: string | number | BigInt
): Promise<ProjectChainState> => {
  const stateData = await contract.projectStateData(toProjectId(projectId))
  const invocations = toNumber(stateData.invocations)
  const maxInvocations = toNumber(stateData.maxInvocations)
  const completedTimestamp = toNumber(stateData.completedTimestamp)

  return {
    invocations,
    maxInvocations,
    active: Boolean(stateData.active),
    paused: Boolean(stateData.paused),
    complete: completedTimestamp > 0 || (maxInvocations > 0 && invocations >= maxInvocations),
  }
}

export const fetchProjectChainState = async (
  contractAddress: string,
  projectId: string | number | BigInt,
  chainId?: number | string | null
) => {
  const resolvedChainId = getContractChainId(contractAddress, chainId)
  const contractConfig = getContractConfigByAddress(contractAddress)

  if (!resolvedChainId || !contractConfig) {
    throw new Error(`Unsupported Art Blocks project contract: ${contractAddress}`)
  }

  return readThroughChainCache(
    makeChainReadCacheKey("art-blocks-project-state", resolvedChainId, contractAddress, projectId.toString()),
    async () => {
      const provider = getReadOnlyProvider(resolvedChainId as ArtBlocksChainId)
      const isV2 = contractConfig.CONTRACT_VERSION.toUpperCase().includes("V2")
      const contract = new ethers.Contract(
        contractAddress,
        isV2 ? GenArt721CoreV2ABI : GenArt721CoreV3EngineABI,
        provider
      )

      return isV2
        ? readV2ProjectState(contract, projectId)
        : readV3ProjectState(contract, projectId)
    }
  )
}

const useProjectChainState = (
  contractAddress: string,
  projectId: string | number | BigInt,
  chainId?: number | string | null
) => {
  const [state, setState] = useState<ProjectChainStateResponse>(initialState)

  useEffect(() => {
    const resolvedChainId = getContractChainId(contractAddress, chainId)
    const contractConfig = getContractConfigByAddress(contractAddress)

    if (!resolvedChainId || !contractConfig) {
      setState(initialState)
      return
    }

    let cancelled = false
    setState({ data: undefined, loading: true, error: false })

    fetchProjectChainState(contractAddress, projectId, resolvedChainId)
      .then((projectState) => {
        if (!cancelled) setState({ data: projectState, loading: false, error: false })
      })
      .catch(() => {
        if (!cancelled) setState({ data: undefined, loading: false, error: true })
      })

    return () => {
      cancelled = true
    }
  }, [chainId, contractAddress, projectId])

  return state
}

export default useProjectChainState
